import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CornerDownLeft, Loader2 } from 'lucide-react';
import { Kbd } from '../ui/kbd';
import { getModeCommand, getModeIcon } from '../../constants/chatModes';
import { ChatMode } from '../../types/chat';
import { cn } from '../../lib/utils';

export interface ChatModeOptionProps {
  mode: ChatMode;
  /** The mode the conversation is in. */
  selected?: boolean;
  /** Keyboard cursor in the slash-command list; not the same as selected. */
  highlighted?: boolean;
  busy?: boolean;
  /** Shows the Enter affordance instead of the command chip. */
  showEnterHint?: boolean;
}

/**
 * One mode, rendered identically wherever it is offered. The mode picker and
 * the slash-command autocomplete are the same feature reached two ways — they
 * previously had two layouts and two icon maps, and the slash list's map was
 * missing `cad` so `/cad` drew a Q&A icon.
 *
 * Content only: each caller supplies its own interactive wrapper, because one
 * is a Radix menu item and the other a plain button in a listbox.
 */
export const ChatModeOptionContent: React.FC<ChatModeOptionProps> = ({
  mode,
  selected = false,
  highlighted = false,
  busy = false,
  showEnterHint = false,
}) => {
  const { t } = useTranslation();
  const Icon = getModeIcon(mode.icon);
  const command = getModeCommand(mode.id);

  return (
    <>
      <span
        className={cn(
          'mt-px grid size-7 shrink-0 place-items-center rounded-md',
          'bg-[color-mix(in_srgb,var(--mode-color)_14%,transparent)]',
          'text-[var(--mode-color)] dark:text-[color-mix(in_srgb,var(--mode-color)_55%,white)]',
        )}
      >
        <Icon className="size-4" strokeWidth={2} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span
            className={cn(
              'truncate text-sm',
              selected &&
                'font-semibold text-[var(--mode-color)] dark:text-[color-mix(in_srgb,var(--mode-color)_60%,white)]',
            )}
          >
            {t(`chat.modes.${mode.id}.name`, mode.name)}
          </span>

          {showEnterHint && highlighted ? (
            <span className="ml-auto flex shrink-0 items-center gap-1 text-[0.6875rem] text-muted-foreground">
              <Kbd>
                <CornerDownLeft className="size-3" strokeWidth={2.2} />
              </Kbd>
              {t('chat.enterKey')}
            </span>
          ) : (
            command && <Kbd className="ml-auto shrink-0 font-mono">{command}</Kbd>
          )}

          {busy && selected ? (
            <Loader2
              className="size-4 shrink-0 animate-spin text-[var(--mode-color)] motion-reduce:animate-none"
              strokeWidth={2}
            />
          ) : (
            selected && (
              <Check
                className="size-4 shrink-0 text-[var(--mode-color)] dark:text-[color-mix(in_srgb,var(--mode-color)_60%,white)]"
                strokeWidth={2.4}
              />
            )
          )}
        </span>

        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
          {t(`chat.modes.${mode.id}.description`, mode.description || '')}
        </span>
      </span>
    </>
  );
};

/** Shared layout and colour wiring for a mode row's interactive wrapper. */
export const modeOptionClassName = (active: boolean) =>
  cn(
    'flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
    'focus:bg-[color-mix(in_srgb,var(--mode-color)_10%,transparent)]',
    'focus-visible:outline-hidden',
    active && 'bg-[color-mix(in_srgb,var(--mode-color)_10%,transparent)]',
  );

/** Per-mode colour, supplied as data rather than a class Tailwind must know. */
export const modeColorStyle = (mode: ChatMode): React.CSSProperties =>
  ({ ['--mode-color' as string]: mode.color }) as React.CSSProperties;
